import { useId, useState } from "react";
import type {
  CustomerPostcodeAddress,
  CustomerPostcodeLookup,
} from "@shared-core/data/customer-basket-types";
import { ApiError } from "@shared-core/data/api/errors";
import { postcodeLookupFailure } from "@shared-core/domain/customer-address-book";
import { Button } from "../../../app/components/primitives/button";
import { Input } from "../../../app/components/primitives/input";
import { Label } from "../../../app/components/primitives/label";
import { Loader2, Search } from "lucide-react";

type LookupState =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "found"; premises: CustomerPostcodeAddress[] }
  | { kind: "refused"; message: string };

/**
 * The add-address form's first step for a United Kingdom address (docs/features/customer-portal-ordering/21 slice 5):
 * type a postcode, choose the address from the list, and the form's fields fill in. "Type it in instead" is always
 * there, so a search that finds nothing, or cannot answer, never stands between the person and adding an address.
 *
 * Only what the address provider knows comes back: nothing here says what the customer already has on file.
 */
export function FindAddressByPostcode({
  find,
  onChoose,
  onUnavailable,
  onTypeInstead,
  disabled,
}: {
  /** Looks the postcode up; rejects with the server's refusal, which this step words for the person. */
  find: (postcode: string) => Promise<CustomerPostcodeLookup>;
  /** The person's chosen premise (the only one, or the one picked from the list). */
  onChoose: (premise: CustomerPostcodeAddress) => void;
  /** The lookup cannot answer at all (not set up, down, not reached): the form falls back to typing, saying why. */
  onUnavailable: (message: string) => void;
  onTypeInstead: () => void;
  disabled: boolean;
}) {
  const id = useId();
  const [postcode, setPostcode] = useState("");
  const [search, setSearch] = useState<LookupState>({ kind: "idle" });
  const searching = search.kind === "searching";
  const canFind = !disabled && !searching && postcode.trim() !== "";
  const refusal = search.kind === "refused" ? search.message : null;

  async function findAddress() {
    if (!canFind) return;
    const searched = postcode.trim();
    setSearch({ kind: "searching" });
    try {
      const { addresses } = await find(searched);
      if (addresses.length === 0) {
        setSearch({
          kind: "refused",
          message: `Nothing found at ${searched.toUpperCase()}: type the address in.`,
        });
      } else if (addresses.length === 1) {
        onChoose(addresses[0]!);
      } else {
        setSearch({ kind: "found", premises: addresses });
      }
    } catch (err) {
      const message = postcodeLookupFailure(err);
      // A refused postcode or a wait is something the person can act on here; anything else is the lookup being
      // out of reach, so the form moves on to typing with the reason beside it.
      const stays =
        err instanceof ApiError && (err.status === 400 || err.status === 429);
      if (stays) setSearch({ kind: "refused", message });
      else onUnavailable(message);
    }
  }

  return (
    <div role="group" aria-label="Find your address" className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-postcode`}>Postcode</Label>
        <div className="flex gap-2">
          <Input
            id={`${id}-postcode`}
            value={postcode}
            disabled={disabled}
            autoComplete="postal-code"
            aria-invalid={refusal ? true : undefined}
            aria-describedby={refusal ? `${id}-refusal` : undefined}
            onChange={(e) => setPostcode(e.target.value)}
            onKeyDown={(e) => {
              // Enter finds the address; it must not submit the form.
              if (e.key !== "Enter") return;
              e.preventDefault();
              void findAddress();
            }}
          />
          <Button
            type="button"
            variant="outline"
            disabled={!canFind}
            onClick={() => void findAddress()}
          >
            {searching ? <Loader2 className="animate-spin" /> : <Search />}
            Find address
          </Button>
        </div>
        {refusal && (
          <p
            id={`${id}-refusal`}
            role="status"
            className="text-sm text-muted-foreground"
          >
            {refusal}
          </p>
        )}
      </div>
      {search.kind === "found" && (
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-premise`}>Choose your address</Label>
          <select
            id={`${id}-premise`}
            defaultValue=""
            disabled={disabled}
            onChange={(e) => {
              const premise = search.premises[Number(e.target.value)];
              if (premise) onChoose(premise);
            }}
            className="border-input bg-input-background focus-visible:border-ring focus-visible:ring-ring/50 h-9 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="" disabled>
              {`${search.premises.length} addresses`}
            </option>
            {search.premises.map((premise, index) => (
              <option key={index} value={index}>
                {premise.label}
              </option>
            ))}
          </select>
        </div>
      )}
      <Button
        type="button"
        variant="link"
        className="h-auto px-0"
        disabled={disabled}
        onClick={onTypeInstead}
      >
        Type it in instead
      </Button>
    </div>
  );
}
